'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import { Menu, X } from 'lucide-react';

const navLinks = [
  { label: 'About', href: '#about' },
  { label: 'Program', href: '#program' },
  { label: 'Modules', href: '#modules' },
  { label: 'Testimonials', href: '#testimonials' },
  { label: 'Webinar', href: '#webinar' },
  { label: 'Pricing', href: '#pricing' },
];

export default function LandingNav() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-card/95 backdrop-blur-md shadow-card border-b border-border' : 'bg-transparent'
      }`}
    >
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-8 xl:px-10">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link href="/" className="flex items-center gap-2.5">
            <AppLogo size={36} />
            <div className="flex flex-col leading-tight">
              <span className="font-bold text-sm text-foreground leading-none">Fluent English</span>
              <span className="font-semibold text-xs text-primary leading-none">Blueprint</span>
            </div>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-1">
            {navLinks?.map((link) => (
              <a
                key={`nav-${link?.label}`}
                href={link?.href}
                className="px-4 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition-all duration-150"
              >
                {link?.label}
              </a>
            ))}
          </nav>

          {/* Desktop Actions */}
          <div className="hidden lg:flex items-center gap-3">
            <Link
              href="/sign-up-login-screen"
              className="px-4 py-2 rounded-lg text-sm font-semibold text-foreground hover:bg-muted transition-all duration-150"
            >
              Sign In
            </Link>
            <a
              href="#webinar"
              className="px-5 py-2.5 rounded-xl text-sm font-semibold text-white btn-press"
              style={{ background: 'var(--secondary)' }}
            >
              Free Masterclass
            </a>
            <Link
              href="/sign-up-login-screen"
              className="px-5 py-2.5 rounded-xl text-sm font-semibold text-white btn-press"
              style={{ background: 'var(--primary)' }}
            >
              Start Free Trial
            </Link>
          </div>

          {/* Mobile Menu Toggle */}
          <button
            className="lg:hidden p-2 rounded-lg hover:bg-muted transition-colors"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileOpen && (
          <div className="lg:hidden py-4 border-t border-border fade-in">
            <nav className="flex flex-col gap-1 mb-4">
              {navLinks?.map((link) => (
                <a
                  key={`mobile-nav-${link?.label}`}
                  href={link?.href}
                  className="px-4 py-3 rounded-xl text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition-all"
                  onClick={() => setMobileOpen(false)}
                >
                  {link?.label}
                </a>
              ))}
            </nav>
            <div className="flex flex-col gap-2 px-1">
              <Link
                href="/sign-up-login-screen"
                className="text-center px-4 py-3 rounded-xl text-sm font-semibold border border-border hover:bg-muted transition-all"
                onClick={() => setMobileOpen(false)}
              >
                Sign In
              </Link>
              <Link
                href="/sign-up-login-screen"
                className="text-center px-4 py-3 rounded-xl text-sm font-semibold text-white btn-press"
                style={{ background: 'var(--primary)' }}
                onClick={() => setMobileOpen(false)}
              >
                Start Your Fluency Journey
              </Link>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}